document.documentElement.classList.remove('no-js');

const qs = (selector, context = document) => context.querySelector(selector);
const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
const config = window.GooeyDuck || {};
let lastFocus = null;

function emit(name, detail = {}) {
  document.dispatchEvent(new CustomEvent(name, { detail }));
}

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(Number(cents || 0) / 100);
}

function escapeHtml(value = '') {
  return String(value).replace(/[&<>'"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
}

function lockPage(locked) {
  document.body.classList.toggle('lock', locked);
}

function setMobileMenu(open) {
  const menu = qs('[data-mobile-menu]');
  const toggle = qs('[data-menu-toggle]');
  if (!menu || !toggle) return;
  menu.classList.toggle('is-open', open);
  menu.setAttribute('aria-hidden', String(!open));
  toggle.setAttribute('aria-expanded', String(open));
  lockPage(open);
  if (open) {
    lastFocus = document.activeElement;
    qs('[data-menu-close]', menu)?.focus();
  } else if (lastFocus) lastFocus.focus();
}

function setDrawer(open) {
  const drawer = qs('[data-cart-drawer]');
  if (!drawer) return;
  const wasOpen = drawer.getAttribute('aria-hidden') === 'false';
  drawer.classList.toggle('is-open', open);
  drawer.setAttribute('aria-hidden', String(!open));
  lockPage(open);
  if (open) {
    lastFocus = document.activeElement;
    qs('[data-drawer-close]', drawer)?.focus();
  } else if (lastFocus) lastFocus.focus();
  if (open !== wasOpen) emit(open ? 'gooey:cart:opened' : 'gooey:cart:closed', { open });
}

async function fetchCart() {
  const response = await fetch(`${config.routes?.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Unable to load cart.');
  return response.json();
}

function updateCartCount(cart) {
  qsa('[data-cart-count]').forEach(node => { node.textContent = cart.item_count; });
}

function cartLineMarkup(item, line) {
  const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<div class="drawer-line__meta">${escapeHtml(item.variant_title)}</div>` : '';
  const image = item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy" decoding="async">` : '';
  return `<div class="drawer-line" data-cart-line="${line}">${image}<div><a class="drawer-line__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>${variant}<div class="drawer-line__qty"><button type="button" data-cart-quantity="${item.quantity - 1}" aria-label="Decrease ${escapeHtml(item.product_title)} quantity"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/></svg></button><span>${item.quantity}</span><button type="button" data-cart-quantity="${item.quantity + 1}" aria-label="Increase ${escapeHtml(item.product_title)} quantity"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M12 5v14"/></svg></button><button class="drawer-line__remove" type="button" data-cart-quantity="0">Remove</button></div></div><strong>${formatMoney(item.final_line_price)}</strong></div>`;
}

function shippingProgressMarkup(cart, threshold) {
  if (!threshold) return '';
  const remaining = Math.max(threshold - cart.total_price, 0);
  const progress = Math.min((cart.total_price / threshold) * 100, 100);
  const message = remaining > 0 ? `${formatMoney(remaining)} away from free shipping.` : 'Free shipping unlocked.';
  return `<div class="shipping-progress"><p>${escapeHtml(message)}</p><div class="shipping-progress__track"><div class="shipping-progress__bar" style="--progress:${progress}%"></div></div></div>`;
}

function renderCartMarkup(cart) {
  const drawer = qs('[data-cart-drawer]');
  const host = qs('#CartDrawerContent');
  if (!drawer || !host) return;
  updateCartCount(cart);
  if (!cart.items.length) {
    host.innerHTML = '<div class="empty-state"><p>Your cart is empty. That\'s fixable.</p><a class="button button--accent button--full" href="/collections/all">Shop the candles</a></div>';
  } else {
    const threshold = Number(drawer.dataset.freeShipping || 0);
    host.innerHTML = `${shippingProgressMarkup(cart, threshold)}${cart.items.map((item, index) => cartLineMarkup(item, index + 1)).join('')}<div class="drawer-summary"><div class="drawer-summary__row"><span>Subtotal</span><strong>${formatMoney(cart.total_price)}</strong></div><small>Shipping and taxes calculated at checkout.</small><a class="button button--accent button--full" href="/checkout" style="margin-top:16px">Checkout</a><a class="button button--outline button--full" href="${escapeHtml(config.routes?.cart || '/cart')}" style="margin-top:9px">View cart</a></div>`;
  }
  emit('gooey:cart:updated', { cart });
}

async function renderCart() {
  const cart = await fetchCart();
  renderCartMarkup(cart);
  return cart;
}

async function changeCartLine(line, quantity) {
  const response = await fetch(`${config.routes?.cartChange || '/cart/change'}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ line, quantity })
  });
  if (!response.ok) throw new Error('Unable to update cart.');
  const cart = await response.json();
  renderCartMarkup(cart);
  return cart;
}

async function addProduct(form) {
  const response = await fetch(`${config.routes?.cartAdd || '/cart/add'}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
  const product = await response.json();
  if (!response.ok) throw new Error(product.description || 'This item could not be added.');
  const cart = await renderCart();
  emit('gooey:product:added', { product, cart });
  return { product, cart };
}

function activateGalleryMedia(section, mediaId) {
  if (!mediaId) return;
  const gallery = qs('[data-product-gallery]', section);
  if (!gallery) return;
  const slides = qsa('[data-product-slide]', gallery);
  const thumbs = qsa('[data-product-thumb]', gallery);
  if (!slides.some(slide => String(slide.dataset.productSlide) === String(mediaId))) return;
  slides.forEach(slide => {
    const active = String(slide.dataset.productSlide) === String(mediaId);
    slide.hidden = !active;
    slide.classList.toggle('is-active', active);
  });
  thumbs.forEach(thumb => {
    const active = String(thumb.dataset.productThumb) === String(mediaId);
    thumb.classList.toggle('is-active', active);
    thumb.setAttribute('aria-pressed', String(active));
  });
}

function initGallery(root = document) {
  qsa('[data-product-gallery]', root).forEach(gallery => {
    if (gallery.dataset.initialized) return;
    gallery.dataset.initialized = 'true';
    const slides = qsa('[data-product-slide]', gallery);
    const thumbs = qsa('[data-product-thumb]', gallery);
    thumbs.forEach(thumb => thumb.addEventListener('click', () => {
      const target = thumb.dataset.productThumb;
      slides.forEach(slide => { const active = slide.dataset.productSlide === target; slide.hidden = !active; slide.classList.toggle('is-active', active); });
      thumbs.forEach(item => { const active = item === thumb; item.classList.toggle('is-active', active); item.setAttribute('aria-pressed', String(active)); });
    }));
  });
}

function initQuantity(root = document) {
  qsa('[data-quantity-picker]', root).forEach(picker => {
    if (picker.dataset.initialized) return;
    picker.dataset.initialized = 'true';
    const input = qs('input', picker);
    qs('[data-quantity-minus]', picker)?.addEventListener('click', () => { input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1); });
    qs('[data-quantity-plus]', picker)?.addEventListener('click', () => { input.value = Number(input.value || 1) + 1; });
  });
}

function updateVariantSpecs(variant) {
  const specs = variant?.specs || {};
  qsa('[data-variant-spec]').forEach(node => {
    const key = node.dataset.variantSpec;
    const rawValue = specs[key];
    const fallback = node.dataset.fallback || '';
    const value = rawValue === null || rawValue === undefined || rawValue === '' ? fallback : rawValue;
    const row = node.closest('[data-spec-row]');
    if (row) row.hidden = value === '' || value === null || value === undefined;
    node.textContent = value === null || value === undefined ? '' : String(value);
  });
}

function updateVariantUI(section, variant) {
  if (!variant) return;
  const compareAt = Number(variant.compare_at_price || 0);
  const price = Number(variant.price || 0);
  qs('[data-product-price]', section).innerHTML = `${formatMoney(price)}${compareAt > price ? `<s>${formatMoney(compareAt)}</s>` : ''}`;
  qsa('[data-add-price]', section).forEach(node => { node.textContent = formatMoney(price); });
  qsa('[data-mobile-price]', section).forEach(node => { node.textContent = formatMoney(price); });
  qsa('[data-mobile-variant]', section).forEach(node => { node.textContent = variant.title; });
  qsa('[data-add-to-cart], [data-submit-main-product]', section).forEach(button => { button.disabled = !variant.available; });
  const label = qs('[data-add-label]', section);
  if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';
  const summary = qs('[data-option-summary]', section);
  if (summary) summary.innerHTML = `Selected: <strong>${escapeHtml(variant.title)}</strong>`;
  activateGalleryMedia(section, variant.featured_media_id);
  updateVariantSpecs(variant);
  const url = new URL(window.location.href);
  url.searchParams.set('variant', variant.id);
  history.replaceState({}, '', url);
}

function initCommitmentSelector(section, variants, select) {
  const buttons = qsa('[data-option-button]', section);
  const groups = qsa('[data-option-group]', section).sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
  if (!buttons.length || !groups.length) return false;

  const initialVariant = variants.find(item => String(item.id) === String(select.value)) || variants.find(item => item.available) || variants[0];
  let selections = initialVariant ? [...initialVariant.options] : [];

  const matchesPrior = (variant, positionIndex) => {
    for (let index = 0; index < positionIndex; index += 1) {
      if (selections[index] && variant.options[index] !== selections[index]) return false;
    }
    return true;
  };

  const normalizeLaterSelections = changedIndex => {
    for (let index = changedIndex + 1; index < groups.length; index += 1) {
      const availableVariants = variants.filter(variant => variant.available && matchesPrior(variant, index));
      if (!availableVariants.length) continue;
      if (!availableVariants.some(variant => variant.options[index] === selections[index])) selections[index] = availableVariants[0].options[index];
    }
  };

  const exactVariant = () => variants.find(variant => variant.options.every((value, index) => value === selections[index]) && variant.available)
    || variants.find(variant => variant.options.every((value, index) => value === selections[index]));

  const render = () => {
    groups.forEach((group, groupIndex) => {
      const position = Number(group.dataset.optionPosition) - 1;
      const groupButtons = qsa('[data-option-button]', group);
      const possibleValues = new Set(variants.filter(variant => matchesPrior(variant, position)).map(variant => variant.options[position]));
      group.hidden = groupIndex > 0 && possibleValues.size <= 1;

      groupButtons.forEach(button => {
        const value = button.dataset.optionValue;
        const candidateAvailable = variants.some(variant => variant.available && matchesPrior(variant, position) && variant.options[position] === value);
        const selected = selections[position] === value;
        button.disabled = !candidateAvailable;
        button.classList.toggle('is-unavailable', !candidateAvailable);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    });

    const variant = exactVariant();
    if (!variant) return;
    select.value = String(variant.id);
    updateVariantUI(section, variant);
  };

  buttons.forEach(button => button.addEventListener('click', () => {
    if (button.disabled) return;
    const index = Number(button.dataset.optionPosition) - 1;
    selections[index] = button.dataset.optionValue;
    normalizeLaterSelections(index);
    render();
  }));

  render();
  return true;
}

function initProduct(root = document) {
  qsa('[data-product-section]', root).forEach(section => {
    if (section.dataset.productInitialized) return;
    section.dataset.productInitialized = 'true';
    const json = qs('[data-product-variants]', section);
    const select = qs('[data-variant-select]', section);
    if (json && select) {
      const variants = JSON.parse(json.textContent);
      const commitmentReady = section.dataset.selectorStyle === 'commitment' && initCommitmentSelector(section, variants, select);
      if (!commitmentReady) {
        const applySelectedVariant = () => {
          const variant = variants.find(item => String(item.id) === String(select.value));
          updateVariantUI(section, variant);
        };
        select.addEventListener('change', applySelectedVariant);
        applySelectedVariant();
      }
    }
    qs('[data-submit-main-product]', section)?.addEventListener('click', () => qs('[data-product-form]', section)?.requestSubmit());
  });
}

function initCollection(root = document) {
  qsa('[data-filter-toggle]', root).forEach(button => {
    if (button.dataset.initialized) return;
    button.dataset.initialized = 'true';
    button.addEventListener('click', () => { const filters = qs('[data-filters]'); const open = filters?.classList.toggle('is-open'); button.setAttribute('aria-expanded', String(Boolean(open))); });
  });
  qsa('[data-sort-select]', root).forEach(select => {
    if (select.dataset.initialized) return;
    select.dataset.initialized = 'true';
    select.addEventListener('change', () => { const url = new URL(window.location.href); url.searchParams.set('sort_by', select.value); window.location.assign(url); });
  });
}

function initScentMatcher(root = document) {
  qsa('[data-scent-matcher]', root).forEach(matcher => {
    if (matcher.dataset.initialized) return;
    matcher.dataset.initialized = 'true';
    const tabs = qsa('[data-match-tab]', matcher);
    const panels = qsa('[data-match-panel]', matcher);
    if (!tabs.length) return;

    const activate = activeTab => {
      const target = activeTab.dataset.matchTab;
      tabs.forEach(tab => {
        const active = tab === activeTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      panels.forEach(panel => {
        const active = panel.dataset.matchPanel === target;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        activate(tabs[nextIndex]);
        tabs[nextIndex].focus();
      });
    });
  });
}

function init(root = document) {
  initGallery(root);
  initQuantity(root);
  initProduct(root);
  initCollection(root);
  initScentMatcher(root);
}

document.addEventListener('click', async event => {
  if (event.target.closest('[data-menu-toggle]')) setMobileMenu(true);
  if (event.target.closest('[data-menu-close]')) setMobileMenu(false);
  if (event.target.closest('[data-drawer-close]')) setDrawer(false);
  if (event.target.closest('[data-cart-open]')) {
    await renderCart();
    setDrawer(true);
  }
  const quantityButton = event.target.closest('[data-cart-quantity]');
  if (quantityButton) {
    const line = Number(quantityButton.closest('[data-cart-line]')?.dataset.cartLine);
    quantityButton.disabled = true;
    try { await changeCartLine(line, Number(quantityButton.dataset.cartQuantity)); }
    catch (error) { console.error(error); }
    finally { quantityButton.disabled = false; }
  }
});

document.addEventListener('submit', async event => {
  const form = event.target.closest('[data-product-form]');
  if (!form || config.cartType !== 'drawer') return;
  event.preventDefault();
  const button = qs('[type=submit]', form);
  const section = form.closest('[data-product-section]');
  const errorHost = section ? qs('[data-product-error]', section) : null;
  if (button) button.disabled = true;
  if (errorHost) { errorHost.hidden = true; errorHost.textContent = ''; }
  try {
    await addProduct(form);
    setDrawer(true);
  } catch (error) {
    if (errorHost) {
      errorHost.textContent = error.message;
      errorHost.hidden = false;
    } else console.error(error);
  } finally {
    if (button) button.disabled = false;
  }
});

document.addEventListener('keydown', event => {
  if (event.key !== 'Escape') return;
  setMobileMenu(false);
  setDrawer(false);
});

document.addEventListener('shopify:section:load', event => init(event.target));
init();